import { Icon, Icons } from '@/components/icon';
import { getIconForColumnType } from '@/components/tables/lib';
import type { TableColumnDefinition } from '@/components/tables/lib';
import {
  type ContactAttributeConfigItem,
  useContactAttributeConfig,
  useUpdateContactAttributeConfig,
} from '@/components/tables/use-contact-attribute-config';
import type { TableDefinitionEntity } from '@/components/tables/use-table-definition';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface ShowColumnsButtonProps {
  columns: TableColumnDefinition[];
  entity: TableDefinitionEntity;
  iconConfig?: Record<string, string>;
  translationConfig?: Record<string, string>;
  pinnedColumns?: string[];
  defaultVisibleColumns?: Set<string>;
  className?: string;
}

export function ShowColumnsButton({
  columns,
  entity,
  iconConfig,
  translationConfig,
  pinnedColumns,
  defaultVisibleColumns,
  className,
}: ShowColumnsButtonProps) {
  const { t } = useTranslation('desktop_ui');
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: configData } = useContactAttributeConfig();
  const updateConfig = useUpdateContactAttributeConfig();

  const configColumns = configData?.data?.columns;

  const getColumnLabel = (col: TableColumnDefinition) => {
    const translationKey = translationConfig?.[col.key];
    return col.label ?? (translationKey ? t(translationKey as never) : col.key);
  };

  const getColumnIcon = (col: TableColumnDefinition): Icons => {
    const iconName = iconConfig?.[col.key];
    if (iconName && iconName in Icons) {
      return Icons[iconName as keyof typeof Icons];
    }
    const hasOptions = (col.options?.length ?? 0) > 0;
    const typeIcon = getIconForColumnType(col.type, { hasOptions });
    return Icons[typeIcon as keyof typeof Icons] ?? Icons.Dash;
  };

  // All toggleable columns (excluding pinned and hidden-by-design)
  const toggleableColumns = useMemo(() => {
    const filtered = (
      pinnedColumns ? columns.filter((col) => !pinnedColumns.includes(col.key)) : columns
    ).filter((col) => !col.hide_column);
    const seen = new Set<string>();
    return filtered.filter((col) => {
      if (seen.has(col.key)) {
        return false;
      }
      seen.add(col.key);
      return true;
    });
  }, [columns, pinnedColumns]);

  // Ordered list: config order for enabled, then disabled — both sorted by display_order
  const orderedColumns = useMemo(() => {
    if (!configColumns || configColumns.length === 0) {
      // No config yet — use hardcoded defaults if provided, otherwise fall back to API is_visible
      return toggleableColumns.map((col) => ({
        col,
        enabled: defaultVisibleColumns
          ? defaultVisibleColumns.has(col.key)
          : col.is_visible !== false,
      }));
    }

    const configByKey = new Map(
      configColumns.map((c: ContactAttributeConfigItem) => [c.column_key, c]),
    );

    const columnsByKey = new Map(toggleableColumns.map((col) => [col.key, col]));
    const enabled: { col: TableColumnDefinition; enabled: boolean; order: number }[] = [];
    const disabled: { col: TableColumnDefinition; enabled: boolean; order: number }[] = [];

    // Columns present in config — use config's is_visible and display_order
    for (const [key, cfg] of configByKey) {
      const col = columnsByKey.get(key);
      if (col) {
        const item = { col, enabled: cfg.is_visible, order: cfg.display_order };
        if (cfg.is_visible) {
          enabled.push(item);
        } else {
          disabled.push(item);
        }
        columnsByKey.delete(key);
      }
    }

    // Remaining columns not in config — treat as disabled
    for (const col of toggleableColumns) {
      if (columnsByKey.has(col.key)) {
        disabled.push({ col, enabled: false, order: Number.MAX_SAFE_INTEGER });
      }
    }

    enabled.sort((a, b) => a.order - b.order);
    disabled.sort((a, b) => a.order - b.order);

    return [...enabled, ...disabled].map(({ col, enabled }) => ({ col, enabled }));
  }, [configColumns, toggleableColumns]);

  // Snapshot on popover open to prevent layout jumps
  const [snapshotOrder, setSnapshotOrder] = useState(orderedColumns);

  useEffect(() => {
    if (isOpen) {
      setSnapshotOrder(orderedColumns);
    }
  }, [isOpen]);

  const isSearching = searchValue.trim().length > 0;
  const searchFilter = (item: { col: TableColumnDefinition }) =>
    getColumnLabel(item.col).toLowerCase().includes(searchValue.toLowerCase().trim());

  const enabledItems = snapshotOrder.filter((item) => item.enabled);
  const disabledItems = snapshotOrder.filter((item) => !item.enabled);
  const filteredEnabled = isSearching ? enabledItems.filter(searchFilter) : enabledItems;
  const filteredDisabled = isSearching ? disabledItems.filter(searchFilter) : disabledItems;

  const persistConfig = useCallback(
    (items: { col: TableColumnDefinition; enabled: boolean }[]) => {
      // Always prepend pinned columns so they're persisted in the config
      const pinnedEntries = (pinnedColumns ?? []).map((key, index) => ({
        column_key: key,
        display_order: index,
        is_visible: true,
      }));
      const offset = pinnedEntries.length;
      const allEntries = items.map((item, index) => ({
        column_key: item.col.key,
        display_order: offset + index,
        is_visible: item.enabled,
      }));
      updateConfig.mutate([...pinnedEntries, ...allEntries]);
    },
    [updateConfig, pinnedColumns],
  );

  const handleToggle = useCallback(
    (colKey: string) => {
      const idx = snapshotOrder.findIndex((item) => item.col.key === colKey);
      if (idx === -1) {
        return;
      }

      const item = snapshotOrder[idx]!;
      const toggled = { col: item.col, enabled: !item.enabled };
      const next = [...snapshotOrder];

      // Remove from current position
      next.splice(idx, 1);
      // Insert at boundary between enabled and disabled
      const lastEnabledIdx = next.findLastIndex((i) => i.enabled);
      next.splice(lastEnabledIdx + 1, 0, toggled);

      setSnapshotOrder(next);
      persistConfig(next);

      analytics.track(ANALYTICS_EVENTS.COLUMNS_TOGGLER.COLUMN_TOGGLED, {
        entity,
        columnKey: colKey,
      });
    },
    [entity, persistConfig, snapshotOrder],
  );

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortableEnabledIds = useMemo(
    () => snapshotOrder.filter((item) => item.enabled).map((item) => item.col.key),
    [snapshotOrder],
  );

  const sortableDisabledIds = useMemo(
    () => snapshotOrder.filter((item) => !item.enabled).map((item) => item.col.key),
    [snapshotOrder],
  );

  const handleEnabledDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      const enabled = snapshotOrder.filter((item) => item.enabled);
      const disabled = snapshotOrder.filter((item) => !item.enabled);

      const oldIndex = enabled.findIndex((item) => item.col.key === active.id);
      const newIndex = enabled.findIndex((item) => item.col.key === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const reordered = arrayMove(enabled, oldIndex, newIndex);
      const next = [...reordered, ...disabled];

      setSnapshotOrder(next);
      persistConfig(next);
    },
    [persistConfig, snapshotOrder],
  );

  const handleDisabledDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      const enabled = snapshotOrder.filter((item) => item.enabled);
      const disabled = snapshotOrder.filter((item) => !item.enabled);

      const oldIndex = disabled.findIndex((item) => item.col.key === active.id);
      const newIndex = disabled.findIndex((item) => item.col.key === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const reordered = arrayMove(disabled, oldIndex, newIndex);
      const next = [...enabled, ...reordered];

      setSnapshotOrder(next);
      persistConfig(next);
    },
    [persistConfig, snapshotOrder],
  );

  const handleOpenChange = (next: boolean) => {
    setIsOpen(next);
    if (!next) {
      setSearchValue('');
    } else {
      analytics.track(ANALYTICS_EVENTS.COLUMNS_TOGGLER.OPENED, { entity });
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-7 cursor-pointer items-center gap-1 rounded-full border border-primary-10 bg-primary-3 px-2 outline-none hover:bg-primary-5',
            'data-[state=open]:bg-primary-7',
            className,
          )}
        >
          <Icon name={Icons.Columns} className="text-m text-primary-70" />
          <P3 className="text-primary-80">{t('components.table.columns.showColumns')}</P3>
          <Icon name={Icons.ChevronDown} className="text-s text-primary-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[240px] rounded-2xl bg-white p-0 shadow-md"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        {/* Search input */}
        <div className="flex h-7 items-center gap-1 px-2 py-1">
          <Icon name={Icons.MagnifyingGlass} className="text-s text-primary-50 leading-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder={t('components.table.columns.searchColumns')}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="flex-1 bg-transparent font-inter font-medium text-primary-100 text-s caret-highlight-default outline-none placeholder:text-primary-60"
          />
        </div>

        {(filteredEnabled.length > 0 || filteredDisabled.length > 0) && (
          <>
            <div className="h-px w-full bg-primary-10" />

            <div className="max-h-[240px] overflow-y-auto scrollbar-hide">
              <div className="flex flex-col gap-0.5 p-1">
                {/* Enabled columns — wrapped in DndContext for reordering */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                  onDragEnd={handleEnabledDragEnd}
                >
                  <SortableContext
                    items={sortableEnabledIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredEnabled.map((item) => (
                      <SortableColumnRow
                        key={item.col.key}
                        col={item.col}
                        icon={getColumnIcon(item.col)}
                        label={getColumnLabel(item.col)}
                        draggable={!isSearching}
                        enabled={true}
                        onToggle={handleToggle}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                {/* Divider */}
                {!isSearching && filteredEnabled.length > 0 && filteredDisabled.length > 0 && (
                  <div className="my-0.5 h-px w-full bg-primary-10" />
                )}

                {/* Disabled columns — also draggable for ordering */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                  onDragEnd={handleDisabledDragEnd}
                >
                  <SortableContext
                    items={sortableDisabledIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredDisabled.map((item) => (
                      <SortableColumnRow
                        key={item.col.key}
                        col={item.col}
                        icon={getColumnIcon(item.col)}
                        label={getColumnLabel(item.col)}
                        draggable={!isSearching}
                        enabled={false}
                        onToggle={handleToggle}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

function SortableColumnRow({
  col,
  icon,
  label,
  draggable,
  enabled,
  onToggle,
}: {
  col: TableColumnDefinition;
  icon: Icons;
  label: string;
  draggable: boolean;
  enabled: boolean;
  onToggle: (key: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.key });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex h-7 w-full items-center justify-between rounded-md px-1 transition-colors hover:bg-primary-3"
    >
      <div
        ref={setActivatorNodeRef}
        className={cn(
          'flex shrink-0 cursor-grab items-center px-0.5 text-primary-30 active:cursor-grabbing',
          !draggable && 'pointer-events-none invisible',
        )}
        {...attributes}
        {...listeners}
      >
        <DragDots />
      </div>

      <button
        type="button"
        onClick={() => onToggle(col.key)}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 px-1"
      >
        <Icon name={icon} className="text-s text-primary-70" />
        <P3 className="truncate text-primary-100">{label}</P3>
      </button>

      <Checkbox
        checked={enabled}
        onCheckedChange={() => onToggle(col.key)}
        className="pointer-events-auto size-3 shrink-0"
      />
    </div>
  );
}

function DragDots() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden="true">
      <circle cx="3" cy="3" r="1.2" />
      <circle cx="7" cy="3" r="1.2" />
      <circle cx="3" cy="7" r="1.2" />
      <circle cx="7" cy="7" r="1.2" />
      <circle cx="3" cy="11" r="1.2" />
      <circle cx="7" cy="11" r="1.2" />
    </svg>
  );
}
